// =============================================================================
// features/meu-whatsapp/ExcludedContactsPanel.tsx — Contatos pessoais
// excluídos da caixa (F51-S12).
//
// Só é montado por quem tem `channel.excluded_contacts:manage` (gate no
// componente pai, `MeuWhatsAppPage`, mesmo padrão de `AiAutoreplyToggle`) —
// esconder aqui é cosmético, quem barra de verdade é a API (D4: só o dono da
// caixa, derivado da sessão, nunca aceita de outro).
//
// `components/ui/**` fora de `files_allowed` deste slot — primitivos locais,
// mesmo padrão de `AiAutoreplyToggle.tsx` (switch local).
//
// LGPD: o input de número é local ao formulário — nunca vai para estado
// global, nunca aparece de volta na lista (só `contact_name_snapshot`, que
// pode ser `null`).
// =============================================================================

import * as React from 'react';

import { cn } from '../../lib/cn';

import {
  CONTACT_REMOTE_ID_RE,
  useExcludedContacts,
  useMarkContactExcluded,
  useUnmarkContactExcluded,
} from './useExcludedContacts';

export interface ExcludedContactsPanelProps {
  readonly enabled: boolean;
}

/** `2026-09-23T12:00:00.000Z` -> `23/09/2026` — sem depender de Intl pesado aqui. */
function formatDateBr(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('pt-BR');
}

export function ExcludedContactsPanel({
  enabled,
}: ExcludedContactsPanelProps): React.JSX.Element | null {
  const [value, setValue] = React.useState('');
  const [formError, setFormError] = React.useState<string | null>(null);

  const list = useExcludedContacts({ enabled });
  const mark = useMarkContactExcluded();
  const unmark = useUnmarkContactExcluded();

  if (!enabled) return null;

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const digits = value.trim();

    if (!CONTACT_REMOTE_ID_RE.test(digits)) {
      setFormError('Use só dígitos, sem "+", espaço ou traço (10 a 15 números).');
      return;
    }

    setFormError(null);
    mark.mutate(digits, {
      onSuccess: () => setValue(''),
    });
  }

  return (
    <div
      className="rounded-md border border-border bg-surface-1 p-5 flex flex-col gap-4"
      style={{ boxShadow: 'var(--elev-1)' }}
    >
      <div>
        <h2
          className="font-display font-bold text-ink leading-tight"
          style={{ fontSize: 'var(--text-lg)' }}
        >
          Contatos pessoais
        </h2>
        <p className="font-sans text-xs text-ink-3 mt-1">
          Marque um contato que não é de trabalho — a conversa some da sua caixa e mensagens novas
          dele deixam de aparecer.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-2 sm:flex-row sm:items-start">
        <div className="flex-1 flex flex-col gap-1">
          <label htmlFor="excluded-contact-input" className="sr-only">
            Número do contato
          </label>
          <input
            id="excluded-contact-input"
            type="text"
            inputMode="numeric"
            placeholder="Só números, ex: 5569999999999"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (formError !== null) setFormError(null);
            }}
            disabled={mark.isPending}
            className={cn(
              'font-mono text-sm px-3 py-2 rounded-sm bg-surface-2',
              'border border-border-strong text-ink',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-azul/30',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          />
          {formError !== null && (
            <span className="font-sans text-xs text-danger" role="alert">
              {formError}
            </span>
          )}
        </div>
        <button
          type="submit"
          disabled={mark.isPending || value.trim() === ''}
          className={cn(
            'font-sans text-sm font-semibold px-4 py-2 rounded-sm',
            'bg-azul text-white',
            'transition-[filter] duration-fast hover:brightness-110',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          )}
        >
          {mark.isPending ? 'Marcando…' : 'Marcar como pessoal'}
        </button>
      </form>

      <div className="flex flex-col gap-2">
        {list.isLoading ? (
          <div
            className="rounded-sm animate-pulse"
            style={{ width: '100%', height: 40, background: 'var(--surface-muted)' }}
            role="status"
            aria-label="Carregando contatos pessoais"
          />
        ) : list.data === undefined || list.data.length === 0 ? (
          <p className="font-sans text-xs text-ink-3">Nenhum contato marcado como pessoal ainda.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {list.data.map((contact) => (
              <li
                key={contact.id}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-sm"
                style={{ background: 'var(--surface-2)' }}
              >
                <div className="flex flex-col min-w-0">
                  <span className="font-sans text-sm text-ink truncate">
                    {contact.contact_name_snapshot ?? 'Contato sem nome salvo'}
                  </span>
                  <span className="font-sans text-xs text-ink-3">
                    Marcado em {formatDateBr(contact.created_at)}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => unmark.mutate(contact.id)}
                  disabled={unmark.isPending}
                  className={cn(
                    'flex-shrink-0 font-sans text-xs font-medium px-3 py-1.5 rounded-sm',
                    'border border-border-strong text-ink',
                    'transition-colors duration-fast hover:bg-surface-hover',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                  )}
                >
                  Desmarcar
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
